import React, { useState, useRef, useEffect } from 'react';
import { View, SettingsSection } from '../types';

interface HeaderProps {
  navigate: (view: View, params?: any) => void;
  onOpenSettings: (section: SettingsSection) => void;
  currentView: View;
  canInstall: boolean;
  promptInstall: () => void;
}

const Header: React.FC<HeaderProps> = ({ navigate, onOpenSettings, currentView, canInstall, promptInstall }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const sections: { id: SettingsSection; title: string; icon: string; }[] = [
    { id: 'personalization', title: 'Personalization', icon: '🎨' },
    { id: 'payments', title: 'Payment Methods', icon: '💳' },
    { id: 'budgeting', title: 'Budgeting', icon: '📊' },
    { id: 'reminders', title: 'Bill Reminders', icon: '🔔' },
    { id: 'subscription', title: 'Subscription', icon: '⭐' },
    { id: 'about', title: 'About & Support', icon: 'ℹ️' },
    { id: 'data', title: 'Data & Tools', icon: '🛠️' },
    { id: 'sync', title: 'Sync Devices', icon: '🔄' },
    { id: 'disclaimer', title: 'Disclaimer & Privacy', icon: '⚖️' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleMenuAction = (action: () => void) => {
    action();
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg sticky top-0 z-40 w-full border-b border-slate-200 dark:border-slate-700">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Left Item */}
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(View.Dashboard)}>
              <svg className="h-8 w-8 text-teal-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 9V7C17 5.89543 16.1046 5 15 5H5C3.89543 5 3 5.89543 3 7V13C3 14.1046 3.89543 15 5 15H7M9 19H19C20.1046 19 21 18.1046 21 17V11C21 9.89543 20.1046 9 19 9H9C7.89543 9 7 9.89543 7 11V17C7 18.1046 7.89543 19 9 19ZM16 14C16 15.1046 15.1046 16 14 16C12.8954 16 12 15.1046 12 14C12 12.8954 12.8954 12 14 12C15.1046 12 16 12.8954 16 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">SharedBills</h1>
            </div>
          </div>

          {/* Spacer/Center Item */}
          <div className="flex-grow flex justify-center">
            {currentView === View.Dashboard && (
              <button
                id="create-new-bill-button"
                onClick={() => navigate(View.CreateBill)}
                className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full"
                aria-label="Create new bill"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
              </button>
            )}
          </div>

          {/* Right Item */}
          <div ref={menuRef} className="relative flex-shrink-0">
            <button onClick={() => setIsMenuOpen(prev => !prev)} className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg" aria-label="More options">
               <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" /></svg>
            </button>
            {isMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-20">
                {sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => handleMenuAction(() => onOpenSettings(section.id))}
                    className="w-full text-left flex items-center justify-between gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      <span>{section.icon}</span>
                      <span>{section.title}</span>
                    </div>
                  </button>
                ))}
                {canInstall && (
                    <>
                        <div className="my-1 h-px bg-slate-100 dark:bg-slate-700"></div>
                        <button
                          onClick={() => handleMenuAction(promptInstall)}
                          className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            <span className="text-base">📥</span>
                            <span>Install App</span>
                        </button>
                    </>
                )}
                <div className="my-1 h-px bg-slate-100 dark:bg-slate-700"></div>
                <button
                  onClick={() => handleMenuAction(() => navigate(View.Settings))}
                  className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    <span>All Settings</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;